<script setup lang="ts">
import MiiaFwConfig from '@/configs/config'
import { computed } from 'vue'
import { useMiiaFwStore } from '@/stores/miiaFwStore'
import { useDisasterStore } from '@/stores/disasterStore'
import HamburgerMenu from '@/components/header/HamburgerMenu.vue'
import { storeToRefs } from 'pinia'
import { blackSloganWithRed } from '@/assets/img/imageBase64'
import { useNetworkStore } from '@/stores/networkStore'
import {
  isRequireDataUpdate,
  offlineDataUpdateProcessHandler
} from '@/modules/offlineDataUpload/processHandler'
import { useCommonModalStore } from '@/stores/commonModalStore'
import { MODAL_RESULT, type ModalResult } from '@/modules/miiaFw/types/components/commonModal'

const miiaFwStore = useMiiaFwStore()
const disasterStore = useDisasterStore()
const { attributes, isAuthorized } = storeToRefs(miiaFwStore)
const { disasterName } = storeToRefs(disasterStore)
const networkStore = useNetworkStore()
const commonModalStore = useCommonModalStore()

const userName = computed(() =>
  isAuthorized.value && attributes.value ? attributes.value.user_name : ''
)
const authorizedDisasterName = computed(() => (isAuthorized.value ? disasterName.value : ''))
const canConnectNetwork = computed(() => {
  return networkStore.getCanConnectNetwork
})

/**
 * システム全体をオンライン状態にする
 * @param なし
 * @returns なし
 */
const trySetOnlineStatus = async () => {
  const isOnline = networkStore.getIsOnline
  if (!isOnline && (await isRequireDataUpdate())) {
    await commonModalStore
      .openConfirmModal({
        title: '未反映のデータが存在します',
        body: '未反映のデータが存在します。<br>アップロードしますか？',
        okTitle: 'はい',
        cancelTitle: 'いいえ'
      })
      .then(async (result: ModalResult) => {
        if (result === MODAL_RESULT.OK) {
          await networkStore.setOnlineStatus()
          await offlineDataUpdateProcessHandler()
        }
      })
      .catch(async (result: ModalResult) => {
        if (result === MODAL_RESULT.CANCEL) {
          await networkStore.setOnlineStatus()
        }
      })
  } else {
    await networkStore.setOnlineStatus()
  }
}
</script>

<template>
  <nav class="navbar navbar-expand-lg">
    <div class="header-margin">
      <div class="header-logo">
        <img
          :src="`data:image/jpg;base64,${blackSloganWithRed}`"
          width="152"
          height="42"
          alt="FUJIFILM Value from Innovation"
        />
      </div>
      <span class="sys-name">{{ MiiaFwConfig.config.sysName }}</span>
      <!-- 未ログイン時はヘッダーにスペースがないのでオフラインステータスの表示はしない -->
      <div v-if="miiaFwStore.isAuthorized && !networkStore.getIsOnline">
        <!-- ボタンとしての操作はしないが、下のボタンと同じUIとするためにclassでbtnを指定する -->
        <span v-if="networkStore.getIsTryingToSwitchOnline" class="btn btn-outline-info btn-sm">
          <b-icon icon="arrow-clockwise" animation="spin" />
          再接続中･･･
        </span>
        <button
          v-else
          class="btn btn-outline-info btn-sm"
          @click="trySetOnlineStatus"
          :disabled="!canConnectNetwork"
        >
          <span class="fa-stack fa-lg" style="font-size: 0.5em">
            <!-- TODO: INVと合わせるのであればcloudのアイコンは枠線のみにしたいが、farを指定すると表示できない -->
            <font-awesome-icon :icon="['fas', 'cloud']" class="fa-stack-2x" />
            <!-- b-iconのslashは長さが短く、slash-lgも使えなかったのでfont-awesome-iconのslashを使う -->
            <font-awesome-icon icon="slash" class="fa-stack-2x" />
          </span>
          オフライン中･･･
        </button>
      </div>
    </div>
    <div class="header-right">
      <span class="disaster-name">{{ authorizedDisasterName }}</span>
      <span class="user-name">{{ userName }}</span>
      <HamburgerMenu />
    </div>
  </nav>
</template>

<style lang="scss" scoped>
nav {
  align-items: center;
  justify-content: space-between;
  background-color: #ffffff;
  box-shadow: 0 4px 12px 0 rgba(19, 37, 85, 0.16);
}

.header-margin {
  display: flex;
  width: 74%;
  align-items: center;
}

.header-right {
  display: flex;
  width: 25%;
  justify-content: right;
  align-items: center;
}

:deep(.header-margin) .commonDropdown {
  width: 100%;
}

.header-logo {
  margin: 12.5px;
  margin-bottom: 9.92px;
}

.sys-name {
  margin-right: 1%;
  font-size: 1.6rem;
  white-space: nowrap;
}

.disaster-name,
.user-name {
  margin-right: 8px;
  font-size: 1.6rem;
  white-space: nowrap;
}

.circle-info {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 3rem;
  height: 3rem;
  border: 1px solid;
  font-size: 2rem;
  border-radius: 50%;
  margin: 10px;
}

.btn:disabled {
  color: gray;
  border-color: gray;
}
</style>
